import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import detectEthereumProvider from '@metamask/detect-provider';
import { ethers } from 'ethers';
import { toast } from 'react-toastify';
import EthereumProvider from '@walletconnect/ethereum-provider';
import { getMetaMaskProvider, refreshWalletDiscovery } from '../utils/contractInteraction';

// Mobile detection helper
export const isMobileBrowser = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Check if we're inside MetaMask's in-app browser
export const isInMetaMaskBrowser = () => {
  return window.ethereum?.isMetaMask && isMobileBrowser();
};

// MetaMask deep link for the current page
export const getMetaMaskDeepLink = () => {
  const url = window.location.href.replace(/^https?:\/\//, '');
  return `https://metamask.app.link/dapp/${url}`;
};

const WalletContext = createContext();

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

export const WalletProvider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [walletType, setWalletType] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [pausePolling, setPausePolling] = useState(false); // Flag to pause polling during signatures
  const [metamaskProvider, setMetamaskProvider] = useState(null); // Store reference to MetaMask provider specifically

  // Don't auto-initialize wallet on page load
  // useEffect(() => {
  //   initializeWallet();
  // }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Use MetaMask provider specifically if available, otherwise fall back to window.ethereum
    const ethProvider = metamaskProvider || getMetaMaskProvider();
    if (!ethProvider) return;

    const handleAccountChange = async (accounts) => {
      // Don't process account changes if polling is paused (during signature requests)
      if (pausePolling) {
        console.log('⏸️  Ignoring account change during paused polling');
        return;
      }
      console.log('Accounts changed:', accounts);
      handleAccountsChanged(accounts);
    };

    const handleChainChange = (chainId) => {
      // Don't process chain changes if polling is paused (during signature requests)
      if (pausePolling) {
        console.log('⏸️  Ignoring chain change during paused polling');
        return;
      }
      console.log('Chain changed:', chainId);
      handleChainChanged(chainId);
    };

    // Listen for account changes on MetaMask provider
    ethProvider.on('accountsChanged', handleAccountChange);
    ethProvider.on('chainChanged', handleChainChange);

    // Poll for changes every 5 seconds as backup (reduced frequency to avoid conflicts)
    const pollInterval = setInterval(async () => {
      try {
        // Only poll if we have a connected account and polling is not paused
        if (!account || !ethProvider || pausePolling) return;

        const accounts = await ethProvider.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          const currentAccount = accounts[0];
          if (account && currentAccount.toLowerCase() !== account.toLowerCase()) {
            console.log('Account change detected via polling');
            handleAccountsChanged(accounts);
          }
        } else if (account) {
          console.log('Wallet disconnected via polling');
          handleAccountsChanged([]);
        }
      } catch (err) {
        // Silently ignore polling errors - they happen when MetaMask is busy with other requests
        // Only log if it's not the common "Unexpected error" during signature requests
        if (!err.message?.includes('Unexpected error')) {
          console.warn('Polling error:', err.message);
        }
      }
    }, 5000);

    return () => {
      clearInterval(pollInterval);
      if (ethProvider) {
        ethProvider.removeListener('accountsChanged', handleAccountChange);
        ethProvider.removeListener('chainChanged', handleChainChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, pausePolling, metamaskProvider]);

  // eslint-disable-next-line no-unused-vars
  const _initializeWallet = async () => {
    try {
      const ethereumProvider = await detectEthereumProvider();

      if (ethereumProvider) {
        const web3Provider = new ethers.providers.Web3Provider(ethereumProvider);
        setProvider(web3Provider);

        // Check if already connected
        const accounts = await ethereumProvider.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          setSigner(web3Provider.getSigner());
          setConnected(true);
          setWalletType('MetaMask');

          const network = await web3Provider.getNetwork();
          setChainId(network.chainId);

          console.log('Wallet initialized with account:', accounts[0]);
        }
        setIsInitialized(true);
      }
    } catch (error) {
      console.error('Error initializing wallet:', error);
      setIsInitialized(true);
    }
  };

  // Connect to EVM wallet - supports MetaMask and Phantom
  const connectWallet = async (preferredWallet = null) => {
    console.log('🔗 Attempting to connect to EVM wallet...', preferredWallet ? `Preferred: ${preferredWallet}` : '');

    // EVM wallet policy: MetaMask only on this path (WalletConnect has its own
    // connect function). Phantom is reserved for Solana and TronLink for Tron —
    // neither is ever used as an EVM signer here. We resolve the genuine
    // MetaMask via EIP-6963 so that with Phantom/TronLink also installed we do
    // not accidentally connect the wrong wallet/address.
    refreshWalletDiscovery();
    const selectedProvider = getMetaMaskProvider();
    const detectedWalletType = 'MetaMask';

    if (!selectedProvider) {
      toast.error('MetaMask not detected. Install MetaMask, or use WalletConnect, for EVM chains.');
      return;
    }
    console.log('🦊 Connecting to MetaMask (EIP-6963/strict)');

    if (!selectedProvider) {
      toast.error('No EVM wallet detected. Please install MetaMask or Phantom.');
      return;
    }

    // Store provider reference for later use
    setMetamaskProvider(selectedProvider);

    setLoading(true);
    try {
      // Respect whatever network the user has selected in their wallet. This is
      // a multi-chain dapp and the deposit/withdraw flows switch to the chain
      // the user picks per-action, so we must NOT force a single default chain
      // on connect. (Previously this force-switched to Sepolia whenever
      // REACT_APP_CHAIN_ID was 11155111, which pulled the wallet off the user's
      // manually chosen network — e.g. Base Sepolia — and caused deposit
      // "Network switch failed" errors.)

      // Now request accounts
      console.log(`📡 Requesting accounts from ${detectedWalletType}...`);
      const accounts = await selectedProvider.request({
        method: 'eth_requestAccounts',
      });
      console.log('📊 Accounts received:', accounts);

      if (accounts.length > 0) {
        // Create a fresh provider using the selected provider
        const web3Provider = new ethers.providers.Web3Provider(selectedProvider);
        setProvider(web3Provider);

        setAccount(accounts[0]);
        setSigner(web3Provider.getSigner());
        setConnected(true);
        setWalletType(detectedWalletType);

        const network = await web3Provider.getNetwork();
        setChainId(network.chainId);
        console.log('🌐 Connected to network:', network);

        toast.success(`${detectedWalletType} connected successfully!`);
      }
    } catch (error) {
      console.error('❌ Error connecting wallet:', error);

      let errorMessage = 'Failed to connect wallet';
      if (error.code === -32002) {
        errorMessage = `${detectedWalletType || 'Wallet'} is already processing a request. Please check your wallet.`;
      } else if (error.code === 4001) {
        errorMessage = 'Connection rejected by user';
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Convenience methods for specific wallet connections
  const connectMetaMask = () => connectWallet('metamask');
  const connectPhantomEVM = () => connectWallet('phantom');

  // WalletConnect v2 connection
  const connectWalletConnect = useCallback(async () => {
    setLoading(true);
    try {
      // WalletConnect project ID - get one free at https://cloud.walletconnect.com
      const projectId = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || '5e89fa069d41e54b44f1271e1b15a1d0';

      const wcProvider = await EthereumProvider.init({
        projectId,
        chains: [11155111], // Sepolia as default
        optionalChains: [1, 97, 137, 84532, 80002, 167009],
        showQrModal: true,
        metadata: {
          name: 'InterNull Protocol',
          description: 'Privacy-preserving treasury dashboard',
          url: window.location.origin,
          icons: [`${window.location.origin}/logo/main-logo.png`],
        },
      });

      await wcProvider.connect();

      const web3Provider = new ethers.providers.Web3Provider(wcProvider);
      const wcSigner = web3Provider.getSigner();
      const accounts = await web3Provider.listAccounts();
      const network = await web3Provider.getNetwork();

      if (accounts.length > 0) {
        setProvider(web3Provider);
        setAccount(accounts[0]);
        setSigner(wcSigner);
        setConnected(true);
        setWalletType('WalletConnect');
        setChainId(network.chainId);
        setMetamaskProvider(wcProvider);

        // Listen for WalletConnect disconnect
        wcProvider.on('disconnect', () => {
          console.log('WalletConnect disconnected');
          setAccount(null);
          setSigner(null);
          setConnected(false);
          setChainId(null);
          setWalletType(null);
          setMetamaskProvider(null);
        });

        wcProvider.on('accountsChanged', (accounts) => {
          if (accounts.length > 0) {
            setAccount(accounts[0]);
          }
        });

        wcProvider.on('chainChanged', (chainId) => {
          setChainId(parseInt(chainId, 16));
        });

        toast.success('WalletConnect connected!');
      }
    } catch (error) {
      console.error('WalletConnect error:', error);
      if (error.message?.includes('User rejected') || error.message?.includes('Connection request reset')) {
        toast.info('Connection cancelled');
      } else {
        toast.error('Failed to connect via WalletConnect');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const disconnectWallet = async () => {
    // Revoke MetaMask permissions so next connect prompts account selection
    const ethProvider = metamaskProvider || getMetaMaskProvider();
    if (ethProvider && walletType !== 'WalletConnect') {
      try {
        await ethProvider.request({
          method: 'wallet_revokePermissions',
          params: [{ eth_accounts: {} }],
        });
      } catch (err) {
        // wallet_revokePermissions not supported on older MetaMask — ignore
        console.warn('Could not revoke permissions:', err.message);
      }
    }

    setAccount(null);
    setSigner(null);
    setProvider(null);
    setConnected(false);
    setChainId(null);
    setWalletType(null);
    setMetamaskProvider(null);
    toast.info('Wallet disconnected');
  };

  const switchAccount = async () => {
    try {
      // EVM = genuine MetaMask only (never raw window.ethereum / Phantom).
      const evm = metamaskProvider || getMetaMaskProvider();
      if (!evm) { toast.error('MetaMask not detected'); return; }
      // Request MetaMask to show account selector
      await evm.request({
        method: 'wallet_requestPermissions',
        params: [{ eth_accounts: {} }]
      });
      // The account change will be detected by the event listener
      toast.info('Select an account in MetaMask');
    } catch (error) {
      if (error.code === 4001) {
        toast.info('Account switch cancelled');
      } else {
        console.error('Failed to switch account:', error);
        toast.error('Failed to open account selector');
      }
    }
  };

  const switchNetwork = async (targetChainId) => {
    // EVM = genuine MetaMask only (never raw window.ethereum / Phantom).
    const evm = metamaskProvider || getMetaMaskProvider();
    if (!evm) return;

    const id = parseInt(targetChainId);
    const chainHex = `0x${id.toString(16)}`;

    const CHAIN_PARAMS = {
      11155111: { chainName: 'Sepolia Testnet', nativeCurrency: { name: 'SepoliaETH', symbol: 'ETH', decimals: 18 }, rpcUrls: [process.env.REACT_APP_RPC_URL || 'https://eth-sepolia.public.blastapi.io'], blockExplorerUrls: ['https://sepolia.etherscan.io'] },
      84532: { chainName: 'Base Sepolia', nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 }, rpcUrls: ['https://sepolia.base.org'], blockExplorerUrls: ['https://sepolia.basescan.org'] },
      80002: { chainName: 'Polygon Amoy Testnet', nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 }, rpcUrls: ['https://rpc-amoy.polygon.technology'], blockExplorerUrls: ['https://amoy.polygonscan.com'] },
      97: { chainName: 'BNB Smart Chain Testnet', nativeCurrency: { name: 'Test BNB', symbol: 'tBNB', decimals: 18 }, rpcUrls: ['https://data-seed-prebsc-1-s1.bnbchain.org:8545'], blockExplorerUrls: ['https://testnet.bscscan.com'] },
      998: { chainName: 'Hyperliquid Testnet', nativeCurrency: { name: 'USD', symbol: 'USD', decimals: 6 }, rpcUrls: ['https://api.hyperliquid-testnet.xyz/evm'], blockExplorerUrls: ['https://explorer.hyperliquid-testnet.xyz'] },
    };

    try {
      await evm.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: chainHex }],
      });
    } catch (error) {
      if (error.code === 4902) {
        const params = CHAIN_PARAMS[id];
        if (params) {
          try {
            await evm.request({
              method: 'wallet_addEthereumChain',
              params: [{ chainId: chainHex, ...params }],
            });
          } catch (addError) {
            console.error('Failed to add network:', addError);
            toast.error('Failed to add network to MetaMask');
          }
        } else {
          toast.error('Unsupported network - please add it manually in MetaMask');
        }
      } else {
        toast.error('Failed to switch network');
      }
    }
  };

  const handleAccountsChanged = async (accounts) => {
    console.log('handleAccountsChanged called with:', accounts);

    if (accounts.length === 0) {
      // Wallet disconnected
      console.log('Wallet disconnected');
      disconnectWallet();
    } else if (!account || accounts[0].toLowerCase() !== account.toLowerCase()) {
      // Account changed
      const newAccount = accounts[0];
      console.log('Account changed from', account, 'to', newAccount);

      // Update account
      setAccount(newAccount);

      // Create new provider and signer using MetaMask provider if available
      const ethProvider = metamaskProvider || getMetaMaskProvider();
      if (ethProvider) {
        const web3Provider = new ethers.providers.Web3Provider(ethProvider);
        setProvider(web3Provider);
        setSigner(web3Provider.getSigner());
        setConnected(true);
        setWalletType('MetaMask');

        // Update network info
        const network = await web3Provider.getNetwork();
        setChainId(network.chainId);

        toast.info(`Switched to account ${newAccount.slice(0, 6)}...${newAccount.slice(-4)}`);
      }
    }
  };

  const handleChainChanged = (chainId) => {
    console.log('Chain changed to:', chainId);
    setChainId(parseInt(chainId, 16));
    // Reinitialize provider instead of reloading - use MetaMask provider if available
    const ethProvider = metamaskProvider || getMetaMaskProvider();
    if (ethProvider) {
      const web3Provider = new ethers.providers.Web3Provider(ethProvider);
      setProvider(web3Provider);
      if (account) {
        setSigner(web3Provider.getSigner());
      }
    }
    toast.info('Network changed');
  };

  const getBalance = async () => {
    if (!provider || !account) return '0';
    
    try {
      const balance = await provider.getBalance(account);
      return ethers.utils.formatEther(balance);
    } catch (error) {
      console.error('Error getting balance:', error);
      return '0';
    }
  };

  const signMessage = async (message) => {
    if (!signer) throw new Error('Wallet not connected');

    try {
      // Pause polling during signature to avoid conflicts
      console.log('⏸️  Pausing wallet polling for signature request');
      setPausePolling(true);

      // Handle both string messages and object messages
      const messageText = typeof message === 'string'
        ? message
        : JSON.stringify(message, Object.keys(message).sort());

      const signature = await signer.signMessage(messageText);

      console.log('▶️  Resuming wallet polling after signature');
      setPausePolling(false);

      return signature;
    } catch (error) {
      // Always resume polling even if signature fails
      console.log('▶️  Resuming wallet polling after signature error');
      setPausePolling(false);

      console.error('Error signing message:', error);
      if (error.code === 4001) {
        throw new Error('User rejected signature request');
      }
      throw error;
    }
  };

  const sendTransaction = async (transaction) => {
    console.log('🔍 sendTransaction called with:', transaction);
    console.log('🔍 Wallet state:', { connected, account, signer: !!signer });
    
    if (!signer) {
      console.error('❌ No signer available');
      throw new Error('Wallet not connected - no signer available');
    }
    
    try {
      console.log('📡 Calling signer.sendTransaction...');
      const tx = await signer.sendTransaction(transaction);
      console.log('✅ Transaction sent successfully:', tx);
      return tx;
    } catch (error) {
      console.error('❌ Error in signer.sendTransaction:', error);
      console.error('❌ Error details:', {
        message: error.message,
        code: error.code,
        data: error.data
      });
      throw error;
    }
  };

  // Wrapper to execute any MetaMask request with polling paused
  const executeWithPausedPolling = async (asyncFn) => {
    try {
      console.log('⏸️  Pausing wallet polling for MetaMask request');
      setPausePolling(true);

      // Wait longer to ensure polling completely stops and MetaMask settles
      await new Promise(resolve => setTimeout(resolve, 500));

      console.log('📞 Executing MetaMask request...');
      const result = await asyncFn();

      console.log('▶️  Resuming wallet polling after MetaMask request');
      setPausePolling(false);

      return result;
    } catch (error) {
      // Always resume polling even if request fails
      console.log('▶️  Resuming wallet polling after MetaMask request error');
      console.error('Error during paused MetaMask request:', error);
      setPausePolling(false);
      throw error;
    }
  };

  const value = {
    account,
    provider,
    signer,
    chainId,
    loading,
    connected,
    walletType,
    isInitialized,
    connectWallet, // Export as connectWallet (auto-detects best wallet)
    connect: connectWallet, // Also export as connect for compatibility
    connectMetaMask, // Connect specifically to MetaMask
    connectPhantomEVM, // Connect specifically to Phantom's EVM support
    connectWalletConnect, // Connect via WalletConnect v2 (QR code / mobile)
    disconnectWallet,
    switchAccount,
    switchNetwork,
    getBalance,
    signMessage,
    sendTransaction,
    executeWithPausedPolling, // Export helper to pause polling during wallet requests
    // Additional utilities for components
    wallet: account ? {
      address: account,
      utils: ethers.utils,
      sendTransaction: sendTransaction
    } : null,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};